import {useState, SyntheticEvent, Fragment, useContext, useEffect} from "react";
import {Autocomplete, Box, Grid, Paper, TextField, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {CHARGING_STATION_STATUS, CURRENT_STATUS_RS, CUR_ATTR_BY_STATUS_RS, CUR_STATUS_REPAIR_SHOP} from "../../data";
import {FormAutocomplete, FormTextField} from "../../common/form-fields-wrappers";
import {Controller, FormProvider } from "react-hook-form";
import { IRsField } from "./types";
import { RS_CATEGORY } from "../config";
import ActionFilters from "./ActionFilter";
import { INameRepairShop } from "../models";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";

const RsFields = ({repairShop, searchRsForm, handleSearch, filterExpandedState}: IRsField) => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);

    const [curAttributes, setCurAttributes] = useState<string[]>(CUR_ATTR_BY_STATUS_RS['DEFAULT']);

    const handleChangeCurStatus = (_e: SyntheticEvent, value: any) => {
        searchRsForm.setValue('currentAttribute', '');
        const curStatus = value as (CUR_STATUS_REPAIR_SHOP | undefined) || 'DEFAULT';
        setCurAttributes(CUR_ATTR_BY_STATUS_RS[curStatus]);
    }

    const searchLbl= "searchRs.form.label";

    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const legalEntities = globalData.user?.legalEntities;

    const {errors} = searchRsForm.formState;

    return (
        <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
            <FormProvider {...searchRsForm}>
                <form onSubmit={searchRsForm.handleSubmit(handleSearch)}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormAutocomplete
                                name="nameCode"
                                control={searchRsForm.control}
                                label={t(`${searchLbl}.rsNaming`)}
                                fullWidth
                                options={[...repairShop, {code:"", description:""}]/*.map(({description}) => description)*/}
                                onSelect={() => searchRsForm.resetField("repairShopDescriptionLike")}
                                isOptionEqualToValue={(option, value) => option?.description === value?.description}
                                getOptionLabel={(option: any) => option?.description}
                                renderOption={ (props, option) => {
                                    if(!option?.code && !option?.description) return <Fragment key={option?.code}></Fragment>
                                    else {return (
                                        <li {...props} key={option?.code}>
                                            {option?.description} {`(${option?.code})`}
                                        </li>
                                    )}
                                }}
                            />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <FormTextField
                                name="identificationCode"
                                control={searchRsForm.control}
                                label={t(`${searchLbl}.rsId`)}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <FormTextField
                                name="nameCode.code"
                                control={searchRsForm.control}
                                label={t(`${searchLbl}.rsSapId`)}
                                fullWidth
                            />
                        </Grid>

                        <ActionFilters
                            filterExpandedState={filterExpandedState}
                            handleSearch={handleSearch}
                            searchRsForm={searchRsForm}
                            setCurAttributes={setCurAttributes}
                        />
                    </Grid>

                    {filterExpandedState[0] &&
                        <Grid container item xs={12} columnSpacing={2} sx={{mt: theme.spacing(2)}}>
                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField
                                    name="repairShopDescriptionLike"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.rsSearch`)}
                                    fullWidth
                                    onChange={() => searchRsForm.resetField("nameCode.description")}
                                    
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormAutocomplete
                                    name="category"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.rsCategory`)}
                                    multiple
                                    options={RS_CATEGORY}
                                    getOptionLabel={(option: any) => option ? t(`${searchLbl}.rsCategoryValue.${option}`) : ''}
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormAutocomplete
                                    name="currentStatus"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.currentStatus`)}
                                    fullWidth
                                    options={CURRENT_STATUS_RS}
                                    onChange={handleChangeCurStatus}
                                    getOptionLabel={(option: any) => option ? t(`${searchLbl}.currentStatusValue.${option}`) : ''}
                                />
                            </Grid>
                            
                            <Grid item xs={6} md={3}>
                                <FormAutocomplete
                                    name="currentAttribute"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.currentAttribute`)}
                                    fullWidth
                                    options={curAttributes}
                                    getOptionLabel={(option: any) => option ? t(`${searchLbl}.currentAttributeValue.${option}`) : ''}
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormTextField
                                    name="region"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.region`)}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormTextField
                                    name="province"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.province`)}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormTextField
                                    name="city"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.city`)}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormTextField
                                    name="chargingStationIdentificationCode"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.chargingStationIdCode`)}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormAutocomplete
                                    name="chargingStationStatus"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.chargingStationStatus`)}
                                    fullWidth
                                    options={CHARGING_STATION_STATUS}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    getOptionLabel={(option) => option ? t(`${searchLbl}.chargingStationStatusValue.${option}`) : ''}
                                />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <FormTextField
                                    name="certificationProtocolIdentificationCode"
                                    control={searchRsForm.control}
                                    label={t(`${searchLbl}.certificationProtocolId`)}
                                    fullWidth
                                />
                            </Grid>

                            {legalEntities.length > 1 &&
                                <Grid item md={3} xs={12}>
                                    <Controller
                                        control={searchRsForm.control}
                                        name="tenants"
                                        render={({field: {onChange, value}}: any) => (
                                            <Autocomplete
                                                limitTags={1}
                                                disableCloseOnSelect={true}
                                                multiple
                                                id={'tenants'}
                                                options={legalEntities}
                                                value={value || []}
                                                getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.name))}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderInput={(params) => (
                                                    <TextField {...params}
                                                                label={t("searchDms.form.label.legalEntities")}
                                                                error={Boolean(errors?.tenants)}
                                                                helperText={t(errors?.tenants?.message as string)}
                                                    />
                                                )}
                                                onChange={(event, value) => {
                                                    let tmpDealers = globalData.user?.dealers?.filter((dealer: any) => {
                                                        if (value?.length > 0) {
                                                            return value?.find((legalEntity: any) => legalEntity.id === dealer.legalEntities[0].id);
                                                        } else {
                                                            return dealer;
                                                        }
                                                    });
                                                    searchRsForm.setValue('dealers', []);
                                                    onChange( value);
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            }
                        </Grid>
                    }
                </form>
            </FormProvider>
        </Paper>
    )
}

export default RsFields;
